import React from 'react';
import { Card } from './ui/Card';
import { ProgressBar } from './ui/ProgressBar';
import { AlertTriangle, Users, PieChart, Scale, MapPin } from 'lucide-react';
import { STATE_JUDICIAL_CONFIG } from '../data/mockData';
interface OwnershipSummaryProps {
  totalPercentage: number;
  primaryMemberCount: number;
  businessIncorporateState?: string;
}
export function OwnershipSummary({
  totalPercentage,
  primaryMemberCount,
  businessIncorporateState
}: OwnershipSummaryProps) {
  const isOverLimit = totalPercentage > 100;
  // Derive judicial classification
  const stateUpper = businessIncorporateState?.trim().toUpperCase();
  const judicialClassification = stateUpper ? STATE_JUDICIAL_CONFIG[stateUpper] ?? null : null;
  const classificationLabel = judicialClassification ?? 'Not Available';
  const isJudicial = judicialClassification === 'Judicial';
  const isNonJudicial = judicialClassification === 'Non-Judicial';
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-127">
      {/* Card 1: Total Ownership */}
      <Card className="flex flex-col justify-center" data-id="element-128">
        <div className="flex items-center gap-3 mb-2" data-id="element-129">
          <div className="p-2 bg-[#eef4ff] rounded text-[#0176d3]" data-id="element-130">
            <PieChart className="w-5 h-5" data-id="element-131" />
          </div>
          <h4 className="text-sm font-medium text-[#3e3e3c]" data-id="element-132">
            Total Ownership
          </h4>
        </div>
        <div className="mt-1" data-id="element-133">
          <div className="flex items-baseline gap-2 mb-2" data-id="element-134">
            <span className={`text-2xl font-bold ${isOverLimit ? 'text-[#ba0517]' : 'text-[#080707]'}`} data-id="element-135">
              {totalPercentage}%
            </span>
            <span className="text-xs text-[#706e6b]" data-id="element-136">of 100%</span>
          </div>
          <ProgressBar value={totalPercentage} data-id="element-137" />
          {isOverLimit && <div className="flex items-center gap-1 mt-2 text-xs text-[#ba0517]" data-id="element-138">
              <AlertTriangle className="w-3 h-3" data-id="element-139" />
              <span data-id="element-140">Exceeds 100% limit</span>
            </div>}
        </div>
      </Card>

      {/* Card 2: Judicial / Non-Judicial */}
      <Card className="flex flex-col justify-center" data-id="element-141">
        <div className="flex items-center gap-3 mb-2" data-id="element-142">
          <div className={`p-2 rounded ${isJudicial ? 'bg-[#fff3cd] text-[#8e5b04]' : isNonJudicial ? 'bg-[#d4edda] text-[#04844b]' : 'bg-[#f3f2f2] text-[#706e6b]'}`} data-id="element-143">
            <Scale className="w-5 h-5" data-id="element-144" />
          </div>
          <h4 className="text-sm font-medium text-[#3e3e3c]" data-id="element-145">
            Foreclosure Type
          </h4>
        </div>
        <div className="mt-1" data-id="element-146">
          <div className="flex items-baseline gap-2 mb-1" data-id="element-147">
            <span className={`text-2xl font-bold ${isJudicial ? 'text-[#8e5b04]' : isNonJudicial ? 'text-[#04844b]' : 'text-[#706e6b]'}`} data-id="element-148">
              {classificationLabel}
            </span>
          </div>
          <div className="flex items-center gap-1.5 mt-1" data-id="element-149">
            <MapPin className="w-3 h-3 text-[#706e6b]" data-id="element-150" />
            <span className="text-xs text-[#706e6b]" data-id="element-151">
              {stateUpper ? `Incorporated in ${stateUpper}` : 'No incorporation state on record'}
            </span>
          </div>
          {!judicialClassification && stateUpper && <div className="flex items-center gap-1 mt-1.5 text-xs text-[#706e6b]" data-id="element-152">
              <AlertTriangle className="w-3 h-3" data-id="element-153" />
              <span data-id="element-154">State not found in configuration</span>
            </div>}
        </div>
      </Card>

      {/* Card 3: Status */}
      <Card className="flex flex-col justify-center bg-[#f3f2f2] border-dashed" data-id="element-155">
        <div className="text-center" data-id="element-156">
          <h4 className="text-sm font-medium text-[#3e3e3c] mb-1" data-id="element-157">Status</h4>
          {isOverLimit ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#fe5c5c] text-white" data-id="element-158">
              Invalid Configuration
            </span> : totalPercentage === 100 ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#04844b] text-white" data-id="element-159">
              Fully Allocated
            </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0176d3] text-white" data-id="element-160">
              Partially Allocated
            </span>}
          <p className="text-xs text-[#706e6b] mt-2" data-id="element-161">
            {isOverLimit ? 'Please adjust ownership percentages.' : `${100 - totalPercentage}% remaining for allocation.`}
          </p>
        </div>
      </Card>
    </div>;
}